import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import { isAuthenticated } from '../utils/auth';
import api from '../services/api';
import { Doctor } from '../types';
import DoctorForm from '../components/Admin/DoctorForm';
import './Doctors.css';

const Doctors: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Doctors');
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchDoctors();
  }, [navigate]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/doctors');
      if (response.data.success) {
        setDoctors(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load doctors');
      }
    } catch (err: any) {
      console.error('Error fetching doctors:', err);
      setError(err.response?.data?.message || 'Failed to load doctors data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const routes: { [key: string]: string } = {
      'Dashboard': '/dashboard',
      'Calls': '/calls',
      'Conversations': '/conversations',
      'Transcriptions': '/transcriptions',
      'Timings': '/timings',
      'Doctors': '/doctors',
      'Pricing': '/pricing',
      'Appointments': '/appointments',
      'Email Config': '/email-config'
    };
    navigate(routes[tab] || '/dashboard');
  };

  const handleEdit = (doctor: Doctor) => {
    setEditingDoctor(doctor);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingDoctor(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingDoctor(null);
  };

  const handleFormSubmit = () => {
    fetchDoctors();
    handleFormClose();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) return;
    try {
      const response = await api.delete(`/admin/doctors/${id}`);
      if (response.data.success) {
        fetchDoctors();
      } else {
        alert(response.data.message || 'Failed to delete doctor');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting doctor');
    }
  };

  if (!isAuthenticated()) return null;

  if (loading) {
    return (
      <div className="doctors-page">
        <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="page-content">
          <div className="loading-container">Loading doctors list...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctors-page">
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="page-content">
        <div className="page-header">
          <h1>👨‍⚕️ Available Doctors</h1>
          <button className="add-button" onClick={handleAdd}>
            + Add New Doctor
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {doctors.length === 0 && !error ? (
          <div className="no-data">
            <p>No doctors added yet.</p>
            <button className="add-button-secondary" onClick={handleAdd}>
              + Add Doctor
            </button>
          </div>
        ) : (
          <div className="doctors-grid">
            {doctors.map((doctor) => (
              <div key={doctor.id} className="doctor-card">
                <div className="doctor-card-header">
                  <div className="doctor-avatar">
                    {doctor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="doctor-status">
                    <span className={`availability-badge ${doctor.is_available ? 'available' : 'unavailable'}`}>
                      {doctor.is_available ? '🟢 Available' : '🔴 Unavailable'}
                    </span>
                  </div>
                </div>
                <div className="doctor-card-body">
                  <h3 className="doctor-name">{doctor.name}</h3>
                  <div className="doctor-specialization">
                    <span className="label">Specialization:</span>
                    <span className="value">{doctor.specialization}</span>
                  </div>
                  <div className="doctor-schedule">
                    <span className="label">Schedule:</span>
                    <span className="value">{doctor.schedule}</span>
                  </div>
                </div>
                <div className="doctor-card-footer">
                  <button className="edit-btn" onClick={() => handleEdit(doctor)}>
                    ✏️ Edit
                  </button>
                  <button className="delete-btn" onClick={() => handleDelete(doctor.id)}>
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <DoctorForm
          doctor={editingDoctor}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default Doctors;